import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ImageHoverPreviewProps {
  imageUrl: string;
  alt: string;
  thumbnailClassName?: string;
  previewSize?: number; // 预览图的尺寸，默认320px
}

const ImageHoverPreview: React.FC<ImageHoverPreviewProps> = ({
  imageUrl,
  alt,
  thumbnailClassName = 'w-16 h-16',
  previewSize = 320
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [previewPosition, setPreviewPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // 默认放在缩略图右侧
    let left = rect.right + 12;
    let top = rect.top;

    // 如果右侧空间不足，放到左侧
    if (left + previewSize > viewportWidth - 20) {
      left = rect.left - previewSize - 12;
    }

    // 如果左侧也不够，放到缩略图上方或下方
    if (left < 20) {
      left = Math.max(20, (viewportWidth - previewSize) / 2);
      // 放到下方
      top = rect.bottom + 12;
      // 如果下方空间不足，放到上方
      if (top + previewSize > viewportHeight - 20) {
        top = rect.top - previewSize - 12;
      }
    }

    // 确保顶部不超出屏幕
    if (top < 20) {
      top = 20;
    }

    // 确保底部不超出屏幕
    if (top + previewSize > viewportHeight - 20) {
      top = viewportHeight - previewSize - 20;
    }

    setPreviewPosition({ top, left });
  }, [previewSize]);

  const handleMouseEnter = useCallback(() => {
    // 清除之前的定时器
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // 延迟100ms后显示预览，避免快速划过时闪烁
    hoverTimeoutRef.current = setTimeout(() => {
      calculatePosition();
      setIsHovering(true);
    }, 100);
  }, [calculatePosition]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(false);
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // 窗口大小变化时重新计算位置
  useEffect(() => {
    if (isHovering) {
      calculatePosition();
    }
  }, [isHovering, calculatePosition]);

  return (
    <>
      <div
        ref={containerRef}
        className={`flex-shrink-0 ${thumbnailClassName} rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 cursor-pointer transition-all duration-200 hover:border-primary hover:shadow-md hover:scale-105`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <img
          src={imageUrl}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      {/* 放大预览浮层 */}
      <AnimatePresence>
        {isHovering && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed z-[9999] pointer-events-none"
            style={{
              top: previewPosition.top,
              left: previewPosition.left,
              width: previewSize,
              height: previewSize
            }}
          >
            <div className="w-full h-full rounded-xl overflow-hidden shadow-2xl border-2 border-white dark:border-gray-700 bg-white dark:bg-gray-800">
              <img
                src={imageUrl}
                alt={alt}
                className="w-full h-full object-contain bg-gray-100 dark:bg-gray-900"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageHoverPreview;

